import React from 'react';
import MarkdownBody from './MarkdownBody';
import InlineQuoteBubble from './InlineQuoteBubble';
import ExternalQuoteBubble from './ExternalQuoteBubble';
import InlineArtifactSection from './InlineArtifactSection';

// Pattern to match {user_profile}, {user_todo}, {user_recent}, {user_recent_raw},
// {user_ai_preferences}, {user_memory}, {user_scratchpad}, {user_intentions} placeholders
// NOTE: recent_raw must come before recent in the alternation to avoid partial matching
const ARTIFACT_PATTERN = /\{user_(profile|todo|recent_raw|recent|ai_preferences|memory|scratchpad|intentions)\}/g;
// Combined pattern for splitting (quotes + external-reference quotes + artifacts)
// NOTE: quote_ext must come before quote in the alternation ({quote: won't
// match {quote_ext: anyway, but keep the specific-first convention).
const COMBINED_PATTERN = /(\{quote_ext:\d+\}|\{quote:\d+\}|\{user_(?:profile|todo|recent_raw|recent|ai_preferences|memory|scratchpad|intentions)\})/g;

/**
 * QuotedContent - Renders content with inline quote previews.
 *
 * Parses the content for {quote:ID} placeholders and renders them
 * as clickable InlineQuoteBubble components.
 *
 * Props:
 *   content: The raw text content that may contain {quote:ID} / {user_profile} / {user_todo} placeholders
 *   quotes: Object mapping quote IDs to quote data (or null if not accessible)
 *   contextArtifacts: Object with "profile" and/or "todo" keys containing artifact data
 *   onQuoteClick: Callback when a quote is clicked (receives quote ID)
 *   nodeId: the node the content belongs to; external-reference bubbles
 *     log what the reader does with them against it (#352)
 *   showRecommendationFeedback: the node is LLM-authored, so its
 *     external-reference quotes are recommendations and get the good /
 *     bad verdict; a user's own quotes get only the read toggle (#363)
 *   onExternalIntroVerdictChange: (itemId, verdict) after the verdict on
 *     a gleaning pick's intro changed (#502)
 */
const QuotedContent = ({ content, quotes, externalQuotes, contextArtifacts, onQuoteClick, onCheckboxToggle, onAddTask, onExternalReadChange, onExternalFeedbackChange, onExternalIntroVerdictChange, nodeId, showRecommendationFeedback = false, referenceBackLabel }) => {
  if (!content) {
    return null;
  }

  // {share_guidance} is a render-time template marker (SHARE_V1). The
  // backend mirrors the LLM-path substitution in
  // contextArtifacts.share_guidance: the proposing-shares guidance for
  // owners with sharing enabled, "" otherwise — so the system-prompt view
  // shows exactly what the model received. Without backend data (older
  // payloads, non-prompt nodes), strip the bare marker.
  const shareGuidance =
    (contextArtifacts && contextArtifacts.share_guidance
      && contextArtifacts.share_guidance.content) || '';
  content = content.replace(
    /\{share_guidance\}\n?/g,
    shareGuidance ? `${shareGuidance}\n` : ''
  );

  // Same render-time mirror for the archive-search guidance (#208).
  const externalGuidance =
    (contextArtifacts && contextArtifacts.external_content_guidance
      && contextArtifacts.external_content_guidance.content) || '';
  content = content.replace(
    /\{external_content_guidance\}\n?/g,
    externalGuidance ? `${externalGuidance}\n` : ''
  );

  const hasQuotes = quotes && Object.keys(quotes).length > 0;
  const hasExternalQuotes = externalQuotes && Object.keys(externalQuotes).length > 0;
  const hasArtifacts = contextArtifacts && Object.keys(contextArtifacts).length > 0;

  // If no special data provided, just render the content as-is with markdown
  if (!hasQuotes && !hasExternalQuotes && !hasArtifacts && !ARTIFACT_PATTERN.test(content)) {
    return (
      <MarkdownBody onCheckboxToggle={onCheckboxToggle} onAddTask={onAddTask}>
        {content}
      </MarkdownBody>
    );
  }

  // Parse content into segments using the combined pattern
  const segments = [];
  const parts = content.split(COMBINED_PATTERN);

  for (const part of parts) {
    if (!part) continue;

    // Check if this part is an external-reference quote placeholder
    const extQuoteMatch = part.match(/^\{quote_ext:(\d+)\}$/);
    if (extQuoteMatch) {
      segments.push({ type: 'external_quote', quoteId: extQuoteMatch[1] });
      continue;
    }

    // Check if this part is a quote placeholder
    const quoteMatch = part.match(/^\{quote:(\d+)\}$/);
    if (quoteMatch) {
      segments.push({ type: 'quote', quoteId: quoteMatch[1] });
      continue;
    }

    // Check if this part is an artifact placeholder
    const artifactMatch = part.match(/^\{user_(profile|todo|recent_raw|recent|ai_preferences|memory|scratchpad|intentions)\}$/);
    if (artifactMatch) {
      segments.push({ type: 'artifact', artifactType: artifactMatch[1] });
      continue;
    }

    // Regular text
    segments.push({ type: 'text', content: part });
  }

  // Render segments
  return (
    <div>
      {segments.map((segment, index) => {
        if (segment.type === 'text') {
          return (
            <MarkdownBody key={index} onCheckboxToggle={onCheckboxToggle} onAddTask={onAddTask}>
              {segment.content}
            </MarkdownBody>
          );
        } else if (segment.type === 'quote') {
          const quoteData = quotes ? quotes[segment.quoteId] : null;
          return (
            <InlineQuoteBubble
              key={index}
              quote={quoteData}
              onClick={onQuoteClick}
            />
          );
        } else if (segment.type === 'external_quote') {
          const quoteData = externalQuotes ? externalQuotes[segment.quoteId] : null;
          // The slot lets a page space quotes as units (a read reply's
          // picks, .read-reply-body) without touching the bubble.
          return (
            <div key={index} className="ext-quote-slot">
              <ExternalQuoteBubble
                quote={quoteData}
                nodeId={nodeId}
                onReadChange={onExternalReadChange}
                onFeedbackChange={onExternalFeedbackChange}
                onIntroVerdictChange={onExternalIntroVerdictChange}
                showRecommendationFeedback={showRecommendationFeedback}
                backLabel={referenceBackLabel}
              />
            </div>
          );
        } else if (segment.type === 'artifact') {
          const artifact = contextArtifacts
            ? contextArtifacts[segment.artifactType]
            : null;
          return (
            <InlineArtifactSection
              key={index}
              type={segment.artifactType}
              artifact={artifact}
            />
          );
        }
        return null;
      })}
    </div>
  );
};


export default QuotedContent;
